import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ListCreditClaimsResponse, type UpdateCreditClaimStatusRequest } from '@sourcito/schemas';
import { apiFetch } from './api';

/** M2.5 credit claims (tenant-side; callers sit behind useModuleEnabled('warehouse')). */

export function useCreditClaims(enabled = true) {
  return useQuery({
    queryKey: ['credit-claims'] as const,
    enabled,
    queryFn: () => apiFetch('/api/credit-claims', { responseSchema: ListCreditClaimsResponse }),
  });
}

/** Phase D deadline urgency for a claim. Null = nothing to flag yet.
 *  A missing deadline is flagged, not skipped — unknown is never safe.
 *  `dueSoonWindowMs` controls how far out "due soon" reaches (default 24h;
 *  the dashboard's attention ledger passes a wider 48h window). */
export function claimDeadlineState(
  deadlineAt: string | null,
  now: Date,
  dueSoonWindowMs = 24 * 60 * 60 * 1000
): { kind: 'overdue' | 'due-soon'; label: string } | null {
  if (!deadlineAt) return { kind: 'overdue', label: 'No deadline' };
  const HOUR = 60 * 60 * 1000;
  const delta = new Date(deadlineAt).getTime() - now.getTime();
  if (delta < 0) {
    const days = Math.floor(-delta / (24 * HOUR));
    return { kind: 'overdue', label: `Overdue ${days < 1 ? '<1d' : `${days}d`}` };
  }
  if (delta <= dueSoonWindowMs) {
    const hours = Math.floor(delta / HOUR);
    return { kind: 'due-soon', label: `Due in ${hours < 1 ? '<1h' : `${hours}h`}` };
  }
  return null;
}

/** Drafts (still need filing) ahead of filed claims; within a status,
 *  earliest deadline first, claims without one last. */
export function compareClaimDeadlines(
  a: { status: string; deadlineAt: string | null },
  b: { status: string; deadlineAt: string | null }
): number {
  if (a.status !== b.status) {
    if (a.status === 'draft') return -1;
    if (b.status === 'draft') return 1;
  }
  if (a.deadlineAt === b.deadlineAt) return 0;
  if (a.deadlineAt == null) return 1;
  if (b.deadlineAt == null) return -1;
  return a.deadlineAt < b.deadlineAt ? -1 : 1;
}

export function useUpdateClaimStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string } & UpdateCreditClaimStatusRequest) =>
      apiFetch(`/api/credit-claims/${vars.id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: vars.status }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['credit-claims'] }),
  });
}
